import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import axios from "axios";
import ThemeToggle from "./ThemeToggle";
import { GESIL_LOGO_URL } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { label: "About", href: "/#about" },
  { label: "Expertise", href: "/#expertise" },
  { label: "AI Solutions", href: "/#ai" },
  { label: "Services", href: "/#services" },
  { label: "Projects", href: "/#projects" },
  { label: "Leadership", href: "/#leadership" },
  { label: "Blogs", href: "/#blogs" },
  { label: "Careers", href: "/#careers" },
];export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [logo, setLogo] = useState(GESIL_LOGO_URL);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    axios.get(`${API}/site-settings`).then((r) => {
      if (r.data?.gesil_logo_url) setLogo(r.data.gesil_logo_url);
    }).catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const hashIdx = href.indexOf("#");
    const targetPath = hashIdx >= 0 ? (href.slice(0, hashIdx) || "/") : href;
    const targetHash = hashIdx >= 0 ? href.slice(hashIdx + 1) : "";
    const doScroll = () => {
      if (!targetHash) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(targetHash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        attempts += 1;
        if (attempts < 30) setTimeout(tryScroll, 80);
      };
      tryScroll();
    };
    if (location.pathname !== targetPath) {
      navigate(href);
      setTimeout(doScroll, 120);
    } else {
      doScroll();
      // update URL hash for share-ability without triggering re-mount
      if (targetHash) window.history.replaceState(null, "", `${targetPath}#${targetHash}`);
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-blue-100 shadow-[0_4px_24px_-16px_rgba(37,99,235,0.18)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-24 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <img
            src={logo}
            alt="GESIL — Global e-Village Services India Ltd · Powering Your Future Bright"
            className="h-16 md:h-20 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <Link
              key={n.label}
              to={n.href}
              onClick={(e) => handleNav(e, n.href)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-3 py-2 text-sm font-medium text-[#0F172A] hover:text-[#2563EB] transition-colors whitespace-nowrap"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle compact />
          <Link
            to="/#internship"
            onClick={(e) => handleNav(e, "/#internship")}
            data-testid="navbar-cta"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] text-white px-5 py-2.5 text-sm font-semibold transition-colors shadow-[0_10px_25px_-10px_rgba(59,130,246,0.6)]"
          >
            Apply for Internship
            <span aria-hidden>→</span>
          </Link>
          <button
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle navigation"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-[#0F172A] hover:text-[#2563EB]"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-blue-100">
          <div className="max-w-7xl mx-auto px-6 py-4 grid gap-1">
            {NAV.map((n) => (
              <Link
                key={n.label}
                to={n.href}
                onClick={(e) => handleNav(e, n.href)}
                className="px-3 py-3 text-sm font-medium text-[#0F172A] hover:bg-blue-50 hover:text-[#2563EB] rounded-md"
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/#internship"
              onClick={(e) => handleNav(e, "/#internship")}
              className="mt-2 px-3 py-3 text-sm font-semibold text-white bg-[#3B82F6] rounded-md text-center"
            >
              Apply for Internship
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
